import { CompanyType } from 'src/app/modules/core/enums/company-types.enum';
import { LinkedPartner } from 'src/app/modules/core/models/linked-partner.model';
import { ModelHelper } from '../classes/model-helper';
import { CompanyState } from 'src/app/modules/core/enums/company-states.enum';
import { AdditionalOrderInformationTemplate } from 'src/app/modules/core/models/additional-order-information-template.model';
import { LabProfileRestorationType } from 'src/app/modules/core/models/lab-profile-restoration-type.model';
import { Model } from '../classes/model';
import { ApprovalState } from '../enums/approval-states.enum';
import { ListRange } from '../enums/list-range';
import { Address } from './address.model';
import { LabLockEntry } from './lab-lock-entry.model';
import { LabProfileMaterialClass } from './lab-profile-material-class.model';
import { LabProfileShade } from './lab-profile-shade.model';
import { Offering } from './offering.model';
import { Service } from './service.model';
import { Timezone } from './timezone.model';
import { Market } from './market.model';
import { Country } from './country.model';

export class Company extends Model {
	uid: string;
	name: string;
	eMailAddress: string;
	phoneNumber: string;
	type: CompanyType;
	approvalState: ApprovalState;
	faxNumber: string;
	website: string;
	languageCode: string;
	externalPartner: LinkedPartner;
	labLockEntries: LabLockEntry[];
	timezone: Timezone;
	listrange: ListRange;
	shippingAddress: Address;
	billingAddress: Address;
	favoriteContractors: Company[];
	market: Market;
	contactUserUid: string;
	materialClasses: LabProfileMaterialClass[];
	restorations: LabProfileRestorationType[];
	shades: LabProfileShade[];
	services: Service[];
	offerings: Offering[];
	additionalOrderInformationTemplates: AdditionalOrderInformationTemplate[];
	distance: number;
	shippingAddressIsEqualToBillingAddress: boolean;
	state: CompanyState;
	anonymizePatientByDefault: boolean;
	availableInCountries: Country[];
	extendedShutdownDate: Date;

	constructor() {
		super();

		this.materialClasses = [];
		this.restorations = [];
		this.favoriteContractors = [];
		this.shades = [];
		this.services = [];
		this.offerings = [];
		this.additionalOrderInformationTemplates = [];
		this.availableInCountries = [];
	}

	/**
	 * Static variant of isShippingAddressEqualToBillingAddress
	 *
	 * @param obj
	 */
	public static isShippingAddressEqualToBillingAddress(obj: {
		[key: string]: any;
		billingAddress: Address;
		shippingAddress: Address;
	}): boolean {
		return Company.map(obj).isShippingAddressEqualToBillingAddress();
	}

	/**
	 * Prepare company data for api
	 *
	 * @param data
	 */
	public getDataForApi(data: any = this): any {
		// Clone data
		let apiData: any = Company.map(JSON.parse(JSON.stringify(data)));

		// Process properties
		for (const [key, value] of Object.entries(apiData)) {
			// Check for own getDataForApi method
			if (value && (<any>value).getDataForApi) {
				apiData[key] = (<any>value).getDataForApi();
			} else {
				apiData[key] = JSON.parse(JSON.stringify(value));
			}
		}

		if (data.materialClasses && data.materialClasses.length > 0) {
			apiData.materialClasses = data.materialClasses.map((i) => i.uid);
		}

		if (data.favoriteContractors && data.favoriteContractors.length > 0) {
			apiData.favoriteContractors = data.favoriteContractors.map((i) => i.uid);
		}

		if (data.restorations && data.restorations.length > 0) {
			apiData.restorations = data.restorations.map((i) => i.uid);
		}

		if (data.shades && data.shades.length > 0) {
			apiData.shades = data.shades.map((i) => i.uid);
		}

		if (data.offerings && data.offerings.length > 0) {
			apiData.offerings = data.offerings.map((i) => i.uid);
		}

		if (data.services && data.services.length > 0) {
			apiData.services = data.services.map((i) => i.uid);
		}

		if (data.availableInCountries && data.availableInCountries.length > 0) {
			apiData.availableInCountries = data.availableInCountries.map((i: Country) => i.code);
		}

		// Convert language property => use code only
		if (data.language !== undefined) {
			apiData.language = {
				code: data.language
			};
		}

		// Reduce api data to original properties of data
		apiData = ModelHelper.intersection(JSON.parse(JSON.stringify(data)), apiData);

		return apiData;
	}

	/**
	 * Mapping function
	 *
	 * @param data
	 * @return Company
	 */
	public map(data: any): this {
		ModelHelper.map(data, this, 'uid');
		ModelHelper.map(data, this, 'name');
		ModelHelper.map(data, this, 'eMailAddress');
		ModelHelper.map(data, this, 'phoneNumber');
		ModelHelper.map(data, this, 'faxNumber');
		ModelHelper.map(data, this, 'website');
		ModelHelper.map(data, this, 'timezone');
		ModelHelper.map(data, this, 'languageCode');
		ModelHelper.map(data, this, 'listrange');
		ModelHelper.map(data, this, 'labLockEntries', LabLockEntry);
		ModelHelper.map(data, this, 'shippingAddress', Address);
		ModelHelper.map(data, this, 'billingAddress', Address);
		ModelHelper.map(data, this, 'contactUserUid');
		ModelHelper.map(data, this, 'favoriteContractors', Company);
		ModelHelper.map(data, this, 'market', Market);
		ModelHelper.map(data, this, 'materialClasses', LabProfileMaterialClass);
		ModelHelper.map(data, this, 'restorations', LabProfileRestorationType);
		ModelHelper.map(data, this, 'shades', LabProfileShade);
		ModelHelper.map(data, this, 'services', Service);
		ModelHelper.map(data, this, 'offerings', Offering);
		ModelHelper.map(data, this, 'additionalOrderInformationTemplates', AdditionalOrderInformationTemplate);
		ModelHelper.map(data, this, 'distance');
		ModelHelper.map(data, this, 'shippingAddressIsEqualToBillingAddress');
		ModelHelper.map(data, this, 'state');
		ModelHelper.map(data, this, 'type');
		ModelHelper.map(data, this, 'approvalState');
		ModelHelper.map(data, this, 'anonymizePatientByDefault');
		ModelHelper.map(data, this, 'externalPartner', LinkedPartner);
		ModelHelper.map(data, this, 'availableInCountries', Country);
		ModelHelper.map(data, this, 'extendedShutdownDate', Date);

		// Remove shipping address for change detection when shipping address is equal to billing address

		/*		If the user changes something in the Billing-/Shippingaddress Component for example the both regions
		to the same value an exception throws. When the save button prepares the Data for the API it creates a new
		Company object with only the changes that where made. So when the user changes both regions to the same value
		and tries to save it the changes will be mapped to the new company object. At the end of the mapping the
		isShippingAddressEqualToBillingAddress() will be called which compares if the shipping and billing address are
		equal. But only the changes are set in this object. And when both regions are the same this method thinks the
		billing and shipping address are the same. And the save method throws an exception because this if sets the
		shipping address to null. This if checks if the object has an uid. If yes the data object is a complete company
		object. If no only the changes where set and this call to the method is not necessary.*/
		if (
			data.hasOwnProperty('uid') &&
			((data && data['shippingAddressIsEqualToBillingAddress'] === true) || this.isShippingAddressEqualToBillingAddress())
		) {
			this.shippingAddress = null;
		}

		return this;
	}

	/**
	 * Check if shipping address is equal to billing address
	 *
	 * @param obj Optional object with the two addresses for billing and shipping, if not set object = this
	 */
	public isShippingAddressEqualToBillingAddress(
		obj: { [key: string]: any; billingAddress: Address; shippingAddress: Address } = this
	): boolean {
		let equal = false;
		if (!obj.shippingAddress) {
			equal = true;
		} else {
			let billingAddress = obj.billingAddress;
			if (billingAddress) {
				billingAddress = billingAddress.getDataForApi();
				if (billingAddress.longitude && billingAddress.latitude) {
					delete billingAddress.latitude;
					delete billingAddress.longitude;
				}
			}
			let shippingAddress = obj.shippingAddress;
			if (shippingAddress) {
				shippingAddress = shippingAddress.getDataForApi();
				if (shippingAddress.longitude && shippingAddress.latitude) {
					delete shippingAddress.longitude;
					delete shippingAddress.latitude;
				}
			}
			if (JSON.stringify(billingAddress) === JSON.stringify(shippingAddress)) {
				equal = true;
			}
		}
		return equal;
	}
}
